package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.PapiPlaceholderAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.pipeline.PlaceholderRegistry

/** Stage4：PAPI 编译期占位符基础语义校验。 */
class PapiChecker(
    private val diag: DiagnosticCollector,
    private val registry: PlaceholderRegistry? = null,
) {
    companion object {
        private const val DEFAULT_MIN_REFRESH_MS = 250L
        private val SHORT_NAME_REGEX = Regex("[A-Za-z_][A-Za-z0-9_]*")
        private val LOCAL_NAVIGATION_METHODS = setOf("openScreen", "back", "close")
    }

    fun check(screen: ScreenAst) {
        if (screen.papiBlocks.size > 1) {
            screen.papiBlocks.drop(1).forEach { block ->
                diag.error(DslError.E713_PAPI_DUPLICATE_BLOCK, block.position)
            }
        }
        val seen = linkedSetOf<String>()
        for (block in screen.papiBlocks) {
            for (placeholder in block.placeholders) {
                checkOne(placeholder, seen)
            }
        }
    }

    private fun checkOne(placeholder: PapiPlaceholderAst, seen: MutableSet<String>) {
        if (!placeholder.shortNameIsIdentifier || !SHORT_NAME_REGEX.matches(placeholder.shortName)) {
            diag.error(DslError.E719_PAPI_SHORT_NAME_INVALID, placeholder.position, placeholder.shortName)
        }
        if (!seen.add(placeholder.shortName)) {
            diag.error(DslError.E714_PAPI_DUPLICATE_SHORT_NAME, placeholder.position, placeholder.shortName)
        }
        checkRefreshMs(placeholder)
        checkRegistry(placeholder)
        checkBind(placeholder)
        checkPipe(placeholder)
        checkOnChange(placeholder)
    }

    private fun checkRefreshMs(placeholder: PapiPlaceholderAst) {
        val refreshMs = placeholder.refreshMs ?: return
        val minRefreshMs = registry?.defaults?.minRefreshMs ?: DEFAULT_MIN_REFRESH_MS
        if (refreshMs <= 0 || refreshMs < minRefreshMs) {
            diag.error(
                DslError.E715_PAPI_REFRESH_MS_INVALID,
                placeholder.position,
                placeholder.shortName,
                refreshMs,
                minRefreshMs,
            )
        }
    }

    private fun checkRegistry(placeholder: PapiPlaceholderAst) {
        val currentRegistry = registry ?: return
        val expression = "%${placeholder.shortName}%"
        val resolution = currentRegistry.resolveAllow(expression)
        if (!resolution.allowed || resolution.denied) {
            diag.error(
                DslError.E716_PAPI_REGISTRY_REJECTED,
                placeholder.position,
                placeholder.shortName,
                expression,
            )
        }
    }

    private fun checkBind(placeholder: PapiPlaceholderAst) {
        if (placeholder.bindExpr != null && placeholder.bindState == null) {
            diag.error(
                DslError.E718_PAPI_FIELD_UNSUPPORTED,
                placeholder.bindExpr.position,
                placeholder.shortName,
                "bind",
                "state.xxx",
            )
        }
    }

    private fun checkPipe(placeholder: PapiPlaceholderAst) {
        val pipeExpr = placeholder.pipeExpr ?: return
        if (placeholder.pipe == null) {
            diag.error(
                DslError.E718_PAPI_FIELD_UNSUPPORTED,
                pipeExpr.position,
                placeholder.shortName,
                "pipe",
                "{ v -> ... }",
            )
            return
        }
        val sideEffect = findPipeSideEffect(placeholder.pipe.body)
        if (sideEffect != null) {
            diag.error(
                DslError.E717_PAPI_PIPE_SIDE_EFFECT,
                placeholder.pipe.position,
                placeholder.shortName,
                sideEffect,
            )
        }
    }

    private fun checkOnChange(placeholder: PapiPlaceholderAst) {
        if (placeholder.onChangeExpr != null && placeholder.onChange == null) {
            diag.error(
                DslError.E718_PAPI_FIELD_UNSUPPORTED,
                placeholder.onChangeExpr.position,
                placeholder.shortName,
                "onChange",
                "::funName",
            )
        }
    }

    private fun findPipeSideEffect(stmts: List<StmtAst>): String? {
        for (stmt in stmts) {
            findStmtSideEffect(stmt)?.let { return it }
        }
        return null
    }

    private fun findStmtSideEffect(stmt: StmtAst): String? = when (stmt) {
        is StmtAst.AssignStmt -> when (stmt.lhs) {
            is top.wcpe.allincore.dsl.compiler.ast.LValueAst.StateLValue -> "state 赋值"
            else -> findExprSideEffect(stmt.rhs)
        }
        is StmtAst.ExprStmt -> findExprSideEffect(stmt.call)
        is StmtAst.VarDeclStmt -> findExprSideEffect(stmt.initializer)
        is StmtAst.IfStmt -> findExprSideEffect(stmt.cond)
            ?: findPipeSideEffect(stmt.thenBody)
            ?: findPipeSideEffect(stmt.elseBody)
        is StmtAst.WhileStmt -> "循环语句"
        is StmtAst.ForInStmt -> "循环语句"
        is StmtAst.TryStmt -> "try 语句"
        // batch 语句：作为副作用语句描述返回（与 TryStmt 同样按整体描述处理）
        is StmtAst.BatchStmt -> "batch 语句"
        is StmtAst.ReturnStmt -> stmt.value?.let(::findExprSideEffect)
        is StmtAst.WhenStmt -> findExprSideEffect(stmt.whenExpr)
        is StmtAst.FunDeclStmt -> "局部函数声明"
        is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> "循环控制语句"
    }

    private fun findExprSideEffect(expr: ExprAst): String? = when (expr) {
        is ExprAst.AwaitExpr -> "await"
        is ExprAst.NetRequestCall -> "net.request"
        is ExprAst.NetSubscribeCall -> "net.subscribe"
        is ExprAst.MemberCall -> findMemberCallSideEffect(expr)
            ?: findExprSideEffect(expr.target)
            ?: expr.args.firstNotNullOfOrNull(::findExprSideEffect)
            ?: expr.namedArgs.firstNotNullOfOrNull { findExprSideEffect(it.value) }
        is ExprAst.SafeMemberCall -> findLocalNavigation(expr.target, expr.method)
            ?: findExprSideEffect(expr.target)
            ?: expr.args.firstNotNullOfOrNull(::findExprSideEffect)
        is ExprAst.CallExpr -> findExprSideEffect(expr.callee)
            ?: expr.args.firstNotNullOfOrNull(::findExprSideEffect)
            ?: expr.namedArgs.firstNotNullOfOrNull { findExprSideEffect(it.value) }
        is ExprAst.BuiltinCallExpr -> expr.args.firstNotNullOfOrNull(::findExprSideEffect)
            ?: expr.namedArgs.firstNotNullOfOrNull { findExprSideEffect(it.value) }
        is ExprAst.BinaryOp -> findExprSideEffect(expr.left) ?: findExprSideEffect(expr.right)
        is ExprAst.UnaryOp -> findExprSideEffect(expr.operand)
        is ExprAst.MemberAccess -> findExprSideEffect(expr.target)
        is ExprAst.SafeMemberAccess -> findExprSideEffect(expr.target)
        // M10 Stage 6：`target!!` 透传 target
        is ExprAst.NonNullAssert -> findExprSideEffect(expr.target)
        is ExprAst.Elvis -> findExprSideEffect(expr.left) ?: findExprSideEffect(expr.right)
        is ExprAst.Ternary -> findExprSideEffect(expr.cond)
            ?: findExprSideEffect(expr.thenExpr)
            ?: findExprSideEffect(expr.elseExpr)
        is ExprAst.IfExpr -> findExprSideEffect(expr.cond)
            ?: findPipeSideEffect(expr.thenBody)
            ?: findPipeSideEffect(expr.elseBody)
        is ExprAst.Interp -> expr.parts.firstNotNullOfOrNull { part ->
            (part as? ExprAst.Interp.InterpPart.ExprPart)?.let { findExprSideEffect(it.expr) }
        }
        is ExprAst.ListLiteralExpr -> expr.elements.firstNotNullOfOrNull(::findExprSideEffect)
        is ExprAst.MapLiteralExpr -> expr.entries.firstNotNullOfOrNull { findExprSideEffect(it.value) }
        is ExprAst.RangeExpr -> findExprSideEffect(expr.start) ?: findExprSideEffect(expr.endValue)
        is ExprAst.IndexExpr -> findExprSideEffect(expr.target) ?: findExprSideEffect(expr.index)
        is ExprAst.WhenExpr -> findExprSideEffect(expr.subject)
            ?: expr.branches.firstNotNullOfOrNull { branch ->
                branch.values.firstNotNullOfOrNull(::findExprSideEffect) ?: findPipeSideEffect(branch.body)
            }
            ?: expr.elseBody?.let(::findPipeSideEffect)
        is ExprAst.LambdaExpr -> findPipeSideEffect(expr.body)
        is ExprAst.IsExpr -> findExprSideEffect(expr.target)
        is ExprAst.Literal,
        is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef,
        is ExprAst.IdentRef,
        is ExprAst.ThemeRef,
        is ExprAst.PropsRef,
        is ExprAst.NullLit,
        is ExprAst.LocalRef,
        is ExprAst.FunRefExpr -> null
    }

    private fun findMemberCallSideEffect(expr: ExprAst.MemberCall): String? =
        findLocalNavigation(expr.target, expr.method)

    private fun findLocalNavigation(target: ExprAst, method: String): String? {
        val ident = target as? ExprAst.IdentRef ?: return null
        if (ident.name == "local" && method in LOCAL_NAVIGATION_METHODS) return "local.$method"
        return null
    }
}
